// ✅ FILE: src/pages/Home.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  BookOpen,
  Shield,
  Users,
  Laptop,
  Leaf,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { API_URL, formatDate } from "../lib/utils";
import "./home-premium.css";

const focusAreas = [
  {
    icon: Heart,
    title: "Humanitarian Relief",
    description:
      "Rapid-response welfare support for vulnerable individuals and families with urgent needs.",
    color: "bg-rose-50 text-rose-600",
  },
  {
    icon: BookOpen,
    title: "Education & Child Development",
    description:
      "Back-to-school interventions and educational support for vulnerable children.",
    color: "bg-blue-50 text-blue-600",
  },

  // ✅ CHANGED: Human Rights & Protection -> Sustainable Agriculture
  {
    icon: Leaf,
    title: "Sustainable Agriculture",
    description:
      "Climate-smart farming support that improves food security, livelihoods, and long-term community stability.",
    color: "bg-green-50 text-green-700",
  },

  {
    icon: Users,
    title: "Empowerment Programs",
    description: "Skills training and support for widows and vulnerable adults.",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: Laptop,
    title: "Technology Inclusion",
    description:
      "Digital literacy and practical tech skills training for communities.",
    color: "bg-amber-50 text-amber-700",
  },
  {
    icon: Shield,
    title: "Climate Action",
    description:
      "Environmental awareness and cleaner-community initiatives.",
    color: "bg-teal-50 text-teal-600",
  },
];

const heroSlides = [
  {
    id: "humanitarian-protection",
    badge: "Humanitarian & Child Protection",
    heading: "Rapid Response. Safe Support. Long-Term Protection.",
    text: "We provide immediate humanitarian assistance while strengthening systems that safeguard children and vulnerable individuals from harm, neglect, abuse, and crisis.",
    ctaLabel: "Explore What We Do",
    ctaTo: "https://www.glgif.org/what-we-do",
    image: "/assets/whatwedo-rights.png",
    overlay: "from-red-950/85 via-red-900/65 to-black/60",
  },
  {
    id: "education-skills-digital",
    badge: "Education, Skills & Digital Inclusion",
    heading: "Unlocking Opportunity Through Knowledge and Technology",
    text: "We expand access to education, vocational skills, and digital literacy to bridge opportunity gaps and promote long-term self-sufficiency.",
    ctaLabel: "Explore What We Do",
    ctaTo: "https://www.glgif.org/what-we-do",
    image: "/assets/whatwedo-education.png",
    overlay: "from-blue-950/85 via-blue-900/65 to-black/60",
  },
  {
    id: "sustainable-agriculture",
    badge: "Sustainable Agriculture",
    heading: "Food Sufficiency. Rural Stability. Economic Strength.",
    text: "We promote sustainable agricultural practices that improve food security, strengthen rural livelihoods, and enhance long-term economic resilience.",
    ctaLabel: "Explore What We Do",
    ctaTo: "https://www.glgif.org/what-we-do",
    image: "/assets/whatwedo-agriculture.png",
    overlay: "from-green-950/85 via-green-900/65 to-black/60",
  },
  {
    id: "climate-resilience",
    badge: "Climate Action & Community Resilience",
    heading: "Protecting Communities from Climate Impacts",
    text: "We promote environmental awareness and strengthen community resilience against climate-related risks and environmental degradation.",
    ctaLabel: "Explore What We Do",
    ctaTo: "https://www.glgif.org/what-we-do",
    image: "/assets/whatwedo-climate.png",
    overlay: "from-teal-950/85 via-teal-900/65 to-black/60",
  },
];

// ✅ Same robust image normalization used for BOTH Events and Blog posts
const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1559027615-cd4628902d4a?auto=format&fit=crop&w=1200&q=85";

// IMPORTANT: build URLs against backend origin (not /api)
const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return FALLBACK_IMAGE;
  const clean = url.trim();
  if (!clean) return FALLBACK_IMAGE;
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

export const Home = () => {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetch(`${API_URL}/seed`, { method: "POST" });

        const statsRes = await fetch(`${API_URL}/stats`);
        const statsData = await statsRes.json();
        setStats(statsData);

        const eventsRes = await fetch(`${API_URL}/events?status=upcoming`);
        const eventsData = await eventsRes.json();
        setEvents(eventsData.slice(0, 3));

        const blogRes = await fetch(`${API_URL}/blog?limit=3`);
        const blogData = await blogRes.json();
        setBlogPosts(blogData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6500);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setActiveSlide((prev) => (prev + 1) % heroSlides.length);

  const prevSlide = () =>
    setActiveSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  return (
    <div data-testid="home-page" className="bg-white text-stone-900">
      {/* Premium Hero Slider */}
      <section className="relative min-h-[92vh] sm:min-h-[90vh]" data-testid="hero-section">
        {heroSlides.map((slide, index) => {
          const isActive = index === activeSlide;
          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                isActive ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${slide.image})` }}
              />
              <div className={`absolute inset-0 bg-gradient-to-br ${slide.overlay}`} />
            </div>
          );
        })}

        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-24 sm:py-28 min-h-[92vh] sm:min-h-[90vh] flex items-center">
          <div className="max-w-3xl">
            <span className="inline-flex items-center rounded-full bg-white/15 border border-white/25 backdrop-blur-sm px-4 py-2 text-xs sm:text-sm font-medium text-white tracking-wide mb-5">
              {heroSlides[activeSlide].badge}
            </span>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.05] whitespace-pre-line">
              {heroSlides[activeSlide].heading}
            </h1>

            <p className="text-white/90 text-base sm:text-lg md:text-xl max-w-2xl mb-8">
              {heroSlides[activeSlide].text}
            </p>

            <Button
              asChild
              className="rounded-full px-8 py-6 text-base sm:text-lg bg-amber-600 hover:bg-amber-700 text-white shadow-xl hover:shadow-2xl transition-all"
            >
              <Link to={heroSlides[activeSlide].ctaTo} data-testid={`hero-${heroSlides[activeSlide].id}-btn`}>
                {heroSlides[activeSlide].ctaLabel}
              </Link>
            </Button>
          </div>
        </div>

        {/* Slider Controls */}
        <div className="absolute z-30 inset-x-0 bottom-8">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveSlide(i)}
                  className={`h-2.5 rounded-full transition-all ${
                    i === activeSlide ? "w-8 bg-white" : "w-2.5 bg-white/50 hover:bg-white/70"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={prevSlide}
                className="w-10 h-10 rounded-full bg-white/15 border border-white/30 text-white backdrop-blur hover:bg-white/25 flex items-center justify-center"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextSlide}
                className="w-10 h-10 rounded-full bg-white/15 border border-white/30 text-white backdrop-blur hover:bg-white/25 flex items-center justify-center"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Intro */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-stone-50 to-white" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-accent text-xl text-amber-700 mb-4">Who We Are</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-stone-900 mb-6">
              GoldenLife Global Impact Foundation Exists So No One Is Left Behind
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed">
              We combine urgent response with long-term transformation. Since 2020, GLGIF has grown from a youth-driven
              dream into a movement restoring dignity, strengthening families, and creating opportunity across communities.
            </p>
          </div>
        </div>
      </section>

      {/* Campaign Spotlight */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="campaign-section">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-700 via-amber-800 to-orange-900" />
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1492176273113-2d51f47b23b0?auto=format&fit=crop&w=1800&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block bg-white/20 border border-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
                2026 Campaign Spotlight
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold mb-6">
                Touch A Thousand Lives <br />
                <span className="text-amber-200">#TATL1000</span>
              </h2>
              <p className="text-white/90 text-lg mb-6">
                The outreach train is moving again. Join us to reach 1,000 lives across communities, cities, states, and villages.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                  <span>Street-to-safety interventions</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                  <span>Clothing & feeding support</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                  <span>Education placement & school support</span>
                </li>
              </ul>
              <Button asChild className="bg-white text-amber-700 hover:bg-white/90 rounded-full px-8 py-6 text-lg">
                <Link to="/tatl1000" data-testid="campaign-join-btn">
                  View Life Changing stories <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/20">
                <img
                  src="/assets/about-main.png"
                  alt="Children we aim to help"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-stone-100">
                <p className="font-heading text-4xl font-bold text-amber-700">1,000</p>
                <p className="text-stone-600">Lives to Touch</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-20 md:py-28" data-testid="focus-areas-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-accent text-xl text-amber-700 mb-4">What We Do</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4">Our Focus Areas</h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              We operate in key areas that deliver immediate relief and sustainable community transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {focusAreas.map((area, index) => (
              <Card
                key={area.title}
                className="group border-stone-200/70 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid={`focus-area-${index}`}
              >
                <CardContent className="p-7">
                  <div
                    className={`w-14 h-14 ${area.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}
                  >
                    <area.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">{area.title}</h3>
                  <p className="text-stone-600 leading-relaxed">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              variant="outline"
              className="border-2 border-amber-700 text-amber-700 hover:bg-amber-50 rounded-full px-8 py-4"
            >
              <Link to="/projects" data-testid="view-projects-btn">
                View All Projects <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      {stats && (
        <section className="py-20 bg-stone-950 text-white" data-testid="stats-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <p className="font-heading text-4xl md:text-5xl font-bold text-amber-400 mb-2">
                  {stats.lives_impacted.toLocaleString()}+
                </p>
                <p className="text-stone-300">Lives Impacted</p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <p className="font-heading text-4xl md:text-5xl font-bold text-amber-400 mb-2">
                  {stats.communities_reached}+
                </p>
                <p className="text-stone-300">Communities Reached</p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <p className="font-heading text-4xl md:text-5xl font-bold text-amber-400 mb-2">
                  {stats.projects || 8}+
                </p>
                <p className="text-stone-300">Active Projects</p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <p className="font-heading text-4xl md:text-5xl font-bold text-amber-400 mb-2">
                  {stats.volunteers || 50}+
                </p>
                <p className="text-stone-300">Volunteers</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      {events.length > 0 && (
        <section className="py-20 md:py-28" data-testid="events-preview-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-accent text-xl text-amber-700 mb-2">Events</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900">Upcoming Events</h2>
              </div>
              <Button asChild variant="ghost" className="text-amber-700 hover:text-amber-800">
                <Link to="/events" data-testid="view-all-events-btn">
                  View All <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event, index) => (
                <Card
                  key={event.id}
                  className="overflow-hidden rounded-2xl border-stone-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid={`event-card-${index}`}
                >
                  <div className="aspect-video relative overflow-hidden group">
                    <img
                      src={normalizeImageUrl(event.image_url)}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                    <span className="absolute top-4 left-4 bg-amber-600 text-white text-sm px-3 py-1 rounded-full">
                      {event.category}
                    </span>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-sm text-amber-700 font-medium mb-2">
                      {formatDate(event.date)} • {event.time}
                    </p>
                    <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">{event.title}</h3>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                    <Link
                      to={`/events/${event.id}`}
                      className="text-amber-700 font-medium hover:text-amber-800 inline-flex items-center"
                    >
                      Learn More <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Preview */}
      {blogPosts.length > 0 && (
        <section className="py-20 md:py-28 bg-stone-50" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-accent text-xl text-amber-700 mb-2">From Our Blog</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900">Impact Stories</h2>
              </div>
              <Button asChild variant="ghost" className="text-amber-700 hover:text-amber-800">
                <Link to="/blog" data-testid="view-all-blog-btn">
                  View All <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <Card
                  key={post.id}
                  className="overflow-hidden rounded-2xl border-stone-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid={`blog-card-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={normalizeImageUrl(post.image_url)}
                      alt={post.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <CardContent className="p-6">
                    <p className="text-sm text-amber-700 font-medium mb-2">{formatDate(post.created_at)}</p>
                    <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">{post.title}</h3>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-amber-700 font-medium hover:text-amber-800 inline-flex items-center"
                    >
                      Read More <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="cta-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=1800&q=85)",
          }}
        >
          <div className="absolute inset-0 bg-blue-950/85" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center text-white">
          <p className="font-accent text-2xl text-amber-400 mb-4">Join Us</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold mb-6">Be Part of the Change</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Stand with us as we restore hope, protect dignity, and create opportunity for vulnerable communities.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 text-lg">
              <Link to="/get-involved#volunteer" data-testid="cta-volunteer-btn">
                Become a Volunteer
              </Link>
            </Button>

            {/* ✅ FIXED: middle button should be BLUE (not white/outline) */}
            <Button
              asChild
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg"
            >
              <Link to="/get-involved#partner" data-testid="cta-partner-btn">
                Become a Partner
              </Link>
            </Button>

            <Button asChild className="bg-white text-blue-900 hover:bg-white/90 rounded-full px-8 py-6 text-lg">
              <Link to="/get-involved#donate" data-testid="cta-donate-btn">
                Donate Now
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;